import { useEffect, useRef, useState, useCallback } from "react";
import { useRoute, Link } from "wouter";
import { useAuth, useUser } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Eraser, PenLine, Type, Undo2, ShieldCheck, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

const TYPED_FONTS = [
  { id: "cursive", label: "Cursive", css: "'Brush Script MT', 'Lucida Handwriting', cursive" },
  { id: "italic", label: "Italic Serif", css: "'Times New Roman', serif" },
  { id: "script", label: "Script", css: "'Segoe Script', 'Comic Sans MS', cursive" },
];

interface Signer {
  email: string;
  name?: string;
  status: "pending" | "sent" | "signed" | "declined";
  signedAt?: string;
  method?: "in_app_draw" | "in_app_typed" | "external";
}

interface SignContext {
  approval: { id: number; contractId: number; currentStage: string; status: string; isAmendment: boolean };
  contract: {
    id: number;
    contractType: string;
    startDate: string;
    endDate: string;
    annualSalary: string;
    signingBonus: string | null;
    status: string;
  };
  coach: { id: number; firstName: string; lastName: string; sport: string; title: string };
  org: { id: number; name: string; institutionName: string | null };
  signature: { id: number; status: string; signers: Signer[] } | null;
  yourSigner: Signer | null;
  allowedSigningMethods: "external_only" | "in_app_only" | "both";
  youAreSigner: boolean;
}

const TYPE_LABEL: Record<string, string> = {
  head_coach: "Head Coach",
  assistant_coach: "Assistant Coach",
  coordinator: "Coordinator",
  interim: "Interim",
};

function formatMoney(v: string | null) {
  if (!v) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function formatDate(s: string) {
  try { return format(new Date(s), "MMM d, yyyy"); } catch { return s; }
}

export default function SignPage() {
  const [, params] = useRoute("/sign/:approvalId");
  const approvalId = params ? parseInt(params.approvalId) : NaN;
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { toast } = useToast();

  const [ctx, setCtx] = useState<SignContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tab, setTab] = useState<"draw" | "type">("draw");
  const [typedName, setTypedName] = useState("");
  const [typedFont, setTypedFont] = useState(TYPED_FONTS[0].id);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const drawing = useRef(false);
  const strokesRef = useRef<Array<Array<{ x: number; y: number }>>>([]);
  const currentStroke = useRef<Array<{ x: number; y: number }>>([]);

  const refresh = useCallback(async () => {
    if (!Number.isFinite(approvalId)) {
      setLoadError("Invalid signing link.");
      setLoading(false);
      return;
    }
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${approvalId}/sign-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { setLoadError("Please sign in to access this signing page."); return; }
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        setLoadError((data as { message?: string }).message ?? "You are not listed as a signer on this contract. Ask the contract administrator to add you if you should be signing.");
        return;
      }
      if (res.status === 404) { setLoadError("This signing request was not found, or you don't have access to it."); return; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setLoadError((data as { error?: string }).error ?? `Error ${res.status}`);
        return;
      }
      const data = (await res.json()) as SignContext;
      setCtx(data);
      if (data.yourSigner?.name) setTypedName(data.yourSigner.name);
      else if (user?.fullName) setTypedName(user.fullName);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load signing page");
    } finally {
      setLoading(false);
    }
  }, [approvalId, getToken, user?.fullName]);

  useEffect(() => {
    if (isLoaded && isSignedIn) void refresh();
    else if (isLoaded && !isSignedIn) { setLoading(false); setLoadError("Please sign in to access this signing page."); }
  }, [isLoaded, isSignedIn, refresh]);

  // Initialize canvas with a white background once the signing UI is mounted,
  // so signatures aren't saved on a transparent canvas.
  useEffect(() => {
    if (!ctx || tab !== "draw") return;
    requestAnimationFrame(() => redrawStrokes());
  }, [ctx, tab]);

  // Canvas drawing helpers
  function redrawStrokes() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx2 = c.getContext("2d");
    if (!ctx2) return;
    ctx2.fillStyle = "#ffffff";
    ctx2.fillRect(0, 0, c.width, c.height);
    ctx2.strokeStyle = "#0a0a0a";
    ctx2.lineWidth = 2.5;
    ctx2.lineCap = "round";
    ctx2.lineJoin = "round";
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue;
      ctx2.beginPath();
      ctx2.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx2.lineTo(stroke[i].x, stroke[i].y);
      ctx2.stroke();
    }
  }
  function clearCanvas() {
    strokesRef.current = [];
    currentStroke.current = [];
    setStrokeCount(0);
    redrawStrokes();
  }
  function undoStroke() {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    redrawStrokes();
  }
  function getPos(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * c.width, y: ((e.clientY - rect.top) / rect.height) * c.height };
  }
  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPos(e);
    currentStroke.current = [{ x, y }];
    const ctx2 = canvasRef.current!.getContext("2d")!;
    ctx2.strokeStyle = "#0a0a0a";
    ctx2.lineWidth = 2.5;
    ctx2.lineCap = "round";
    ctx2.lineJoin = "round";
    ctx2.beginPath();
    ctx2.moveTo(x, y);
    canvasRef.current!.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const { x, y } = getPos(e);
    currentStroke.current.push({ x, y });
    const ctx2 = canvasRef.current!.getContext("2d")!;
    ctx2.lineTo(x, y);
    ctx2.stroke();
  }
  function onPointerUp() {
    if (drawing.current && currentStroke.current.length > 0) {
      strokesRef.current.push(currentStroke.current);
      currentStroke.current = [];
      setStrokeCount(strokesRef.current.length);
    }
    drawing.current = false;
  }

  function renderTypedToCanvas(): string {
    const w = 600, h = 200;
    const off = document.createElement("canvas");
    off.width = w; off.height = h;
    const c = off.getContext("2d")!;
    c.fillStyle = "#ffffff";
    c.fillRect(0, 0, w, h);
    c.fillStyle = "#0a0a0a";
    const fontCss = TYPED_FONTS.find((f) => f.id === typedFont)?.css ?? "cursive";
    c.font = `48px ${fontCss}`;
    c.textBaseline = "middle";
    c.textAlign = "center";
    c.fillText(typedName.trim(), w / 2, h / 2);
    return off.toDataURL("image/png");
  }

  const hasInk = strokeCount > 0;
  const canSubmit = !!ctx && consent && !submitting && (tab === "draw" ? hasInk : typedName.trim().length > 0);

  async function handleSubmit() {
    if (!ctx) return;
    let dataUrl = "";
    if (tab === "draw") {
      if (!hasInk || !canvasRef.current) {
        toast({ title: "Signature required", description: "Please draw your signature.", variant: "destructive" });
        return;
      }
      dataUrl = canvasRef.current.toDataURL("image/png");
    } else {
      if (!typedName.trim()) {
        toast({ title: "Name required", description: "Please type your full legal name.", variant: "destructive" });
        return;
      }
      dataUrl = renderTypedToCanvas();
    }
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/approvals/${ctx.approval.id}/signature/sign-in-app`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          method: tab === "draw" ? "in_app_draw" : "in_app_typed",
          imageBase64: dataUrl,
          typedName: tab === "type" ? typedName.trim() : undefined,
          typedFont: tab === "type" ? typedFont : undefined,
          consent: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error ?? `Error ${res.status}`);
      toast({ title: "Signed", description: "Your signature has been recorded." });
      await refresh();
    } catch (err) {
      toast({ title: "Sign failed", description: err instanceof Error ? err.message : "Error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full" />
          <div>
            <div className="font-bold text-sm tracking-tight">Zindeal</div>
            <div className="text-xs text-muted-foreground">Contract signing</div>
          </div>
          <div className="ml-auto text-xs text-muted-foreground hidden sm:block">
            {user?.primaryEmailAddress?.emailAddress}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {loading && <Skeleton className="h-64 w-full" />}

        {!loading && loadError && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" /> Cannot open signing page
              </CardTitle>
              <CardDescription>{loadError}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline"><Link href="/">Go home</Link></Button>
            </CardContent>
          </Card>
        )}

        {!loading && !loadError && ctx && (
          <>
            {/* Contract summary */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle>
                      Contract for {ctx.coach.firstName} {ctx.coach.lastName}
                    </CardTitle>
                    <CardDescription>
                      {TYPE_LABEL[ctx.contract.contractType] ?? ctx.contract.contractType} ·{" "}
                      {ctx.coach.title} · {ctx.coach.sport}
                      {ctx.org.institutionName && <> · {ctx.org.institutionName}</>}
                    </CardDescription>
                  </div>
                  {ctx.approval.isAmendment && <Badge variant="outline">Amendment</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <dt className="text-xs text-muted-foreground">Start</dt>
                    <dd className="font-medium">{formatDate(ctx.contract.startDate)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">End</dt>
                    <dd className="font-medium">{formatDate(ctx.contract.endDate)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Annual salary</dt>
                    <dd className="font-medium">{formatMoney(ctx.contract.annualSalary)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Signing bonus</dt>
                    <dd className="font-medium">{formatMoney(ctx.contract.signingBonus)}</dd>
                  </div>
                </dl>
                <Separator className="my-4" />
                <p className="text-xs text-muted-foreground">
                  This is a read-only summary. The full executed contract PDF will be available after all signers
                  have completed signing. <Link href={`/contracts/${ctx.contract.id}`} className="text-primary hover:underline">View full contract</Link>.
                </p>
              </CardContent>
            </Card>

            {/* Signing UI */}
            {!ctx.youAreSigner ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" /> You are not a signer on this contract
                  </CardTitle>
                  <CardDescription>
                    Your email ({user?.primaryEmailAddress?.emailAddress}) does not match any signer on this envelope.
                    If you believe this is an error, ask the contract administrator to add you as a signer.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : ctx.yourSigner?.status === "signed" ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-5 w-5" /> You have already signed
                  </CardTitle>
                  <CardDescription>
                    {ctx.yourSigner.signedAt && <>Signed on {format(new Date(ctx.yourSigner.signedAt), "MMM d, yyyy h:mm a")}.</>}
                    {" "}Thank you. You can close this page.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : ctx.allowedSigningMethods === "external_only" ? (
              <Card>
                <CardHeader>
                  <CardTitle>External signing required</CardTitle>
                  <CardDescription>
                    This organization requires signers to use the external e-signature provider.
                    Please check your email for the signing link.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PenLine className="h-5 w-5" /> Sign the contract
                  </CardTitle>
                  <CardDescription>
                    Choose Draw or Type. By signing, you agree your electronic signature is the legal equivalent of
                    your handwritten signature and that signing is binding.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Tabs value={tab} onValueChange={(v) => setTab(v as "draw" | "type")}>
                    <TabsList className="grid grid-cols-2 w-full max-w-xs">
                      <TabsTrigger value="draw"><PenLine className="h-4 w-4 mr-1" /> Draw</TabsTrigger>
                      <TabsTrigger value="type"><Type className="h-4 w-4 mr-1" /> Type</TabsTrigger>
                    </TabsList>
                    <TabsContent value="draw" className="space-y-2">
                      <div className="border rounded-md bg-white">
                        <canvas
                          ref={canvasRef}
                          width={600}
                          height={220}
                          className="w-full h-[180px] sm:h-[220px] touch-none cursor-crosshair"
                          onPointerDown={onPointerDown}
                          onPointerMove={onPointerMove}
                          onPointerUp={onPointerUp}
                          onPointerLeave={onPointerUp}
                        />
                      </div>
                      <div className="flex justify-between items-center flex-wrap gap-2">
                        <p className="text-xs text-muted-foreground">Draw with mouse, finger, or stylus.</p>
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="sm" onClick={undoStroke} disabled={strokeCount === 0}>
                            <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={clearCanvas} disabled={strokeCount === 0}>
                            <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="type" className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="typed-name">Full legal name</Label>
                        <Input
                          id="typed-name"
                          value={typedName}
                          onChange={(e) => setTypedName(e.target.value)}
                          placeholder="Jane Doe"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Style</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {TYPED_FONTS.map((f) => (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => setTypedFont(f.id)}
                              className={`border rounded-md p-3 text-center hover:bg-muted/50 ${typedFont === f.id ? "ring-2 ring-primary border-primary" : ""}`}
                            >
                              <span style={{ fontFamily: f.css, fontSize: 22 }}>{typedName.trim() || "Signature"}</span>
                              <div className="text-xs text-muted-foreground mt-1">{f.label}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <div className="flex items-start gap-2 pt-2">
                    <Checkbox id="esign-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} />
                    <Label htmlFor="esign-consent" className="text-sm font-normal cursor-pointer">
                      I agree that my electronic signature is the legal equivalent of my handwritten signature and
                      that signing is binding. I consent to conduct business electronically.
                    </Label>
                  </div>

                  <div className="flex justify-end pt-1">
                    <Button onClick={handleSubmit} disabled={!canSubmit} size="lg">
                      {submitting ? "Signing…" : "Apply signature"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
